import { inject, Injectable } from '@angular/core';
import { GptCompletionMessage } from '../../common/general-types/open-ai.dto';
import { firstValueFrom, Observable } from 'rxjs';
import { ChatGptService } from '../chat-gpt/chat-gpt.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CreateProductDto, UgcProductResponseDto } from '../../common/general-types/products.dto';

export type { CreateProductDto, UgcProductResponseDto } from '../../common/general-types/products.dto';

export interface AppDetailsDto {
  id: number;
  title: string;
  bundle_id: string;
  seller_link: string;
  description: string;
  link: string;
  screenshots: { link: string; size: string }[];
  logos: { link: string; size: string }[];
  trackName?: string;
  artworkUrl100?: string;
  bundleId?: string;
  trackId?: number;
}

export interface PlayAppDetailsDto {
  title: string;
  link: string;
  product_id: string;
  serpapi_link: string;
  rating: number;
  reviews: number;
  content_rating: { text: string; thumbnail: string };
  downloads: string;
  author: string;
  description: string;
  thumbnail: string;
  images: string[];
  offers: { text: string; link: string }[];
  appId?: string;
}
import { FirebaseService } from '../../common/firebase/firebase.service';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  addProduct: boolean = false;

  allProductTypes: { id: number, name: string, desc: string, colour: string }[] = [
    { id: 0, name: 'Physical product', desc: 'Products / merchandise', colour: 'text-emerald-600' },
    { id: 1, name: 'Digital service', desc: 'Services / platforms', colour: 'text-accent' },
    { id: 2, name: 'Mobile App', desc: 'iOS or Android applications', colour: 'text-blue-600' },
    { id: 3, name: 'Event/Tickets', desc: 'In-person experiences', colour: 'text-orange-600' },
    { id: 4, name: 'Brand', desc: 'This a brand or store', colour: 'text-cyan-600' },
  ];

  private chatGptService = inject(ChatGptService);
  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);

  /**
   * @deprecated Broken for URL lookups: this sends the raw URL to a non-browsing GPT model,
   * which cannot fetch external pages. SpeedySetup now uses {@link getProductDetails} (the
   * backend layered scraper + LLM enrichment). Still referenced by brand-products-create.
   */
  async openAiProductSearch(url: string) {
    const messages: GptCompletionMessage[] = [
      {
        "role": "user",
        "content": `Please extract the product name, description, product category, product industry, product value as a number, currency of the product price and an image from this website:${url}.
        Please choose only 1 value for 'product_category' and 'product_industry'. Return the result as a JSON object with the fields: 'product_name', 'description', 'product_value', 'product_currency', 'image_url', 'product_category' and 'product_industry'.`
      }
    ]

    const gpt_response: any = await firstValueFrom(this.chatGptService.sendCompletionMessage(messages));
    let gptProductResult: any = JSON.parse(gpt_response.choices[0].message.content);
    return gptProductResult;
  }

  createUserProduct(newProduct: CreateProductDto) {
    const create_product_url = `${environment.shout_api.url}/ugc-products`;
    return this.http.post<UgcProductResponseDto>(create_product_url, newProduct, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  editUserProduct(selectedProduct: UgcProductResponseDto) {
    const edit_product_url = `${environment.shout_api.url}/ugc-products/${selectedProduct.id}`;
    return this.http.put<UgcProductResponseDto>(edit_product_url, selectedProduct, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getbusinessProducts(businessAccountId: string) {
    const get_business_products_url = `${environment.shout_api.url}/ugc-products/business-products/${businessAccountId}`;
    return this.http.get(get_business_products_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /** UGC catalog for a business; `shopifyProduct` true = Shopify-synced rows only. */
  filterBusinessProducts(businessAccountId: string, shopifyProduct: boolean) {
    const url = `${environment.shout_api.url}/ugc-products/filter-products`;
    return this.http.post<UgcProductResponseDto[]>(
      url,
      {
        businessAccountId,
        shopify_product: shopifyProduct,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  appStoreSearch(searchTerm: string): Observable<AppDetailsDto[]> {
    const get_app_store_search_url = `${environment.shout_api.url}/serp-apps/apple/${searchTerm}`;
    return this.http.get<AppDetailsDto[]>(get_app_store_search_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  playStoreSearch(searchTerm: string): Observable<PlayAppDetailsDto[]> {
    const get_play_store_search_url = `${environment.shout_api.url}/serp-apps/google/${searchTerm}`;
    return this.http.get<PlayAppDetailsDto[]>(get_play_store_search_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  removeProduct(productId: string) {
    const delete_product_url = `${environment.shout_api.url}/ugc-products/${productId}`;
    return this.http.delete(delete_product_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getProductDetails(url: string, brandName: string) {
    const get_product_details_url = `${environment.shout_api.url}/ugc-products/scraper/product-details-scraper`;
    return this.http.post(get_product_details_url, {
      productUrl: url,
      brandName: brandName
    }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Download an external image URL server-side and save it to our S3 bucket.
   * Returns the permanent S3 URL so we never store third-party image links.
   */
  uploadImageFromUrl(imageUrl: string) {
    const url = `${environment.shout_api.url}/ugc-products/scraper/upload-image-from-url`;
    return this.http.post<{ s3Url: string }>(url, { imageUrl }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getProductById(productId: string) {
    const url = `${environment.shout_api.url}/ugc-products/${productId}`;
    return this.http.get<UgcProductResponseDto>(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getShopifyProductsForBusiness(businessAccountId: string) {
    const url = `${environment.shout_api.url}/affiliate/shopify-products/${businessAccountId}`;
    return this.http.get<unknown>(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  createShopifyProductForBrief(payload: {
    businessAccountId: string;
    shop: string;
    shopifyProductGid: string;
    title: string;
    imageUrl: string | null;
    priceCents: number;
    currency: string;
  }) {
    const url = `${environment.shout_api.url}/shopify-products`;
    return this.http.post<UgcProductResponseDto>(url, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  createRakutenProductForBrief(payload: {
    businessAccountId: string;
    creatorId: string;
    rakutenProductSku: string;
    rakutenProductId: string;
    title: string;
    imageUrl: string | null;
    priceCents: number;
    currency: string;
    website: string;
  }) {
    const url = `${environment.shout_api.url}/ugc-products/upsert-rakuten`;
    return this.http.post<UgcProductResponseDto>(url, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }
}
